import {
  DoorVisitDateRangePreset as GraphQLDoorVisitDateRangePreset,
  DoorVisitListSort as GraphQLDoorVisitListSort,
} from '../../graphql/__generated__/graphql';

export { getDefaultCustomDateRange as getDefaultDoorVisitCustomDateRange } from './calendarDateRange';

export type DoorVisitDateRangePreset = 'all' | 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';

export const DATE_RANGE_OPTIONS: { value: DoorVisitDateRangePreset; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom' },
];

export type DoorVisitSort = 'newest' | 'oldest';

export const SORT_OPTIONS: { value: DoorVisitSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

export type DoorVisitFilterState = {
  dateRange: DoorVisitDateRangePreset;
  fromDate?: string;
  throughDate?: string;
  canvasserId: string | null;
  customSlotName: string | null;
  customSlotValue: string | null;
  sort: DoorVisitSort;
};

export const DEFAULT_FILTERS: DoorVisitFilterState = {
  dateRange: 'all',
  canvasserId: null,
  customSlotName: null,
  customSlotValue: null,
  sort: 'newest',
};

export type DoorVisitListFilterInput = {
  dateRange: GraphQLDoorVisitDateRangePreset;
  fromDate?: string;
  throughDate?: string;
  canvasserId?: string;
  customSlotName?: string;
  customSlotValue?: string;
};

export type DoorVisitNormalizedQueryVariables = {
  filter: DoorVisitListFilterInput;
  sort: GraphQLDoorVisitListSort;
};

const DATE_RANGE_PRESETS: Record<Exclude<DoorVisitDateRangePreset, 'custom'>, GraphQLDoorVisitDateRangePreset> = {
  all: GraphQLDoorVisitDateRangePreset.AllTime,
  last_7_days: GraphQLDoorVisitDateRangePreset.Last_7Days,
  last_30_days: GraphQLDoorVisitDateRangePreset.Last_30Days,
  last_90_days: GraphQLDoorVisitDateRangePreset.Last_90Days,
};

export type DoorVisitCustomDateErrors = {
  fromDate: string | null;
  throughDate: string | null;
};

const isCalendarDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || year > 9_999 || month < 1 || month > 12) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return daysInMonth !== undefined && day >= 1 && day <= daysInMonth;
};

export const getDoorVisitCustomDateErrors = (
  fromDate: string | undefined,
  throughDate: string | undefined,
): DoorVisitCustomDateErrors => {
  const errors: DoorVisitCustomDateErrors = {
    fromDate: !fromDate ? 'From date is required.' : !isCalendarDate(fromDate) ? 'Enter a valid calendar date.' : null,
    throughDate: !throughDate
      ? 'Through date is required.'
      : !isCalendarDate(throughDate)
        ? 'Enter a valid calendar date.'
        : null,
  };

  if (errors.fromDate === null && errors.throughDate === null && fromDate !== undefined && throughDate !== undefined && fromDate > throughDate) {
    errors.fromDate = 'From date must be on or before Through date.';
  }
  return errors;
};

export const isDoorVisitCustomDateRangeValid = (fromDate: string | undefined, throughDate: string | undefined): boolean => {
  const errors = getDoorVisitCustomDateErrors(fromDate, throughDate);
  return errors.fromDate === null && errors.throughDate === null;
};

// The server owns brokerage-local timezone and calendar-bound computation for
// every browser date preset.
export const buildDoorVisitListFilter = (filters: DoorVisitFilterState): DoorVisitListFilterInput => {
  const filter: DoorVisitListFilterInput = filters.dateRange === 'custom'
    ? {
        dateRange: GraphQLDoorVisitDateRangePreset.Custom,
        ...(filters.fromDate !== undefined ? { fromDate: filters.fromDate } : {}),
        ...(filters.throughDate !== undefined ? { throughDate: filters.throughDate } : {}),
      }
    : { dateRange: DATE_RANGE_PRESETS[filters.dateRange] };
  if (filters.canvasserId) filter.canvasserId = filters.canvasserId;
  if (filters.customSlotName) {
    filter.customSlotName = filters.customSlotName;
    // Server requires customSlotValue only ride alongside customSlotName.
    if (filters.customSlotValue) filter.customSlotValue = filters.customSlotValue;
  }

  return filter;
};

// The paginated Door Visit list and report creation deliberately share this
// mapping. Pagination is added only by the list caller, so an export always
// represents the complete normalized URL result rather than its visible page.
export const buildDoorVisitNormalizedQueryVariables = (
  filters: DoorVisitFilterState,
): DoorVisitNormalizedQueryVariables => ({
  filter: buildDoorVisitListFilter(filters),
  sort: filters.sort === 'oldest' ? GraphQLDoorVisitListSort.Oldest : GraphQLDoorVisitListSort.Newest,
});

const SEARCH_PARAM_KEYS = {
  dateRange: 'range',
  fromDate: 'from',
  throughDate: 'through',
  canvasserId: 'canvasser',
  customSlotName: 'slot',
  customSlotValue: 'slotValue',
  sort: 'sort',
} as const;

const isDateRangePreset = (value: string): value is DoorVisitDateRangePreset =>
  DATE_RANGE_OPTIONS.some((option) => option.value === value);

const isSort = (value: string): value is DoorVisitSort => SORT_OPTIONS.some((option) => option.value === value);

export const parseFilters = (searchParams: URLSearchParams): DoorVisitFilterState => {
  const rangeParam = searchParams.get(SEARCH_PARAM_KEYS.dateRange);
  const canvasserParam = searchParams.get(SEARCH_PARAM_KEYS.canvasserId);
  const slotParam = searchParams.get(SEARCH_PARAM_KEYS.customSlotName);
  const slotValueParam = searchParams.get(SEARCH_PARAM_KEYS.customSlotValue);
  const sortParam = searchParams.get(SEARCH_PARAM_KEYS.sort);
  const fromDateParam = searchParams.get(SEARCH_PARAM_KEYS.fromDate);
  const throughDateParam = searchParams.get(SEARCH_PARAM_KEYS.throughDate);
  const dateRange = rangeParam && isDateRangePreset(rangeParam) ? rangeParam : DEFAULT_FILTERS.dateRange;

  return {
    dateRange,
    ...(dateRange === 'custom'
      ? {
          fromDate: fromDateParam ?? undefined,
          throughDate: throughDateParam ?? undefined,
        }
      : {}),
    canvasserId: canvasserParam || DEFAULT_FILTERS.canvasserId,
    customSlotName: slotParam || DEFAULT_FILTERS.customSlotName,
    customSlotValue: slotParam && slotValueParam ? slotValueParam : DEFAULT_FILTERS.customSlotValue,
    sort: sortParam && isSort(sortParam) ? sortParam : DEFAULT_FILTERS.sort,
  };
};

// Defaults are omitted from the query string so the unfiltered URL stays
// clean and "filters cleared" produces an empty (not default-populated) set
// of params.
export const filtersToSearchParams = (filters: DoorVisitFilterState): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.dateRange !== DEFAULT_FILTERS.dateRange) params.set(SEARCH_PARAM_KEYS.dateRange, filters.dateRange);
  if (filters.dateRange === 'custom') {
    if (filters.fromDate !== undefined) params.set(SEARCH_PARAM_KEYS.fromDate, filters.fromDate);
    if (filters.throughDate !== undefined) params.set(SEARCH_PARAM_KEYS.throughDate, filters.throughDate);
  }
  if (filters.canvasserId) params.set(SEARCH_PARAM_KEYS.canvasserId, filters.canvasserId);
  if (filters.customSlotName) {
    params.set(SEARCH_PARAM_KEYS.customSlotName, filters.customSlotName);
    if (filters.customSlotValue) params.set(SEARCH_PARAM_KEYS.customSlotValue, filters.customSlotValue);
  }
  if (filters.sort !== DEFAULT_FILTERS.sort) params.set(SEARCH_PARAM_KEYS.sort, filters.sort);

  return params;
};

// Sort is a display preference, not a narrowing filter, so it doesn't count
// toward "active filters" or get a chip / included in clear-all.
export const hasActiveFilters = (filters: DoorVisitFilterState): boolean =>
  filters.dateRange !== DEFAULT_FILTERS.dateRange ||
  filters.canvasserId !== null ||
  filters.customSlotName !== null;

export const clearFilters = (filters: DoorVisitFilterState): DoorVisitFilterState => ({
  ...DEFAULT_FILTERS,
  sort: filters.sort,
});

export type DoorVisitFilterChip = {
  key: 'dateRange' | 'canvasserId' | 'customSlot';
  label: string;
};

export const buildFilterChips = (
  filters: DoorVisitFilterState,
  resolveCustomSlotLabel?: (name: string) => string | undefined,
  resolveCustomSlotValueLabel?: (name: string, value: string) => string | undefined,
  resolveCanvasserLabel?: (canvasserId: string) => string,
): DoorVisitFilterChip[] => {
  const chips: DoorVisitFilterChip[] = [];

  if (filters.dateRange !== DEFAULT_FILTERS.dateRange) {
    const option = DATE_RANGE_OPTIONS.find((candidate) => candidate.value === filters.dateRange);
    const label = filters.dateRange === 'custom'
      ? `Custom: ${filters.fromDate ?? '—'} – ${filters.throughDate ?? '—'}`
      : option?.label ?? filters.dateRange;
    chips.push({ key: 'dateRange', label });
  }
  if (filters.canvasserId) {
    chips.push({ key: 'canvasserId', label: `Canvasser: ${resolveCanvasserLabel?.(filters.canvasserId) ?? filters.canvasserId}` });
  }
  if (filters.customSlotName) {
    const title = resolveCustomSlotLabel?.(filters.customSlotName) ?? filters.customSlotName;
    const valueLabel = filters.customSlotValue
      ? (resolveCustomSlotValueLabel?.(filters.customSlotName, filters.customSlotValue) ?? filters.customSlotValue)
      : undefined;
    chips.push({
      key: 'customSlot',
      label: valueLabel ? `${title}: ${valueLabel}` : title,
    });
  }

  return chips;
};

export const clearFilterChip = (filters: DoorVisitFilterState, key: DoorVisitFilterChip['key']): DoorVisitFilterState => {
  switch (key) {
    case 'dateRange':
      return { ...filters, dateRange: DEFAULT_FILTERS.dateRange, fromDate: undefined, throughDate: undefined };
    case 'canvasserId':
      return { ...filters, canvasserId: null };
    case 'customSlot':
      return { ...filters, customSlotName: null, customSlotValue: null };
  }
};
